import type { TransmitterConfig } from '../types';

interface LoRaWANOtaProfile {
  supportsAutomatedCommands: boolean;
  estimateCommandCount?: (configs: TransmitterConfig[]) => number;
}

interface LoRaWANOtaNoticeOptions {
  deviceType?: string;
  transmissionIntervalSeconds?: number;
}

export interface LoRaWANOtaNotice {
  buttonTitle: string;
  pendingText: string;
}

const DRAGINO_COMMON_COMMAND_KEYS = new Set([
  'time_sync_enabled',
  'pnackmd',
  'led_alarm',
]);
const DRAGINO_LHT65_COMMAND_KEYS = new Set([
  'ext_sensor_mode',
  'probe_id_reporting',
]);
const DRAGINO_INTERVAL_KEYS = new Set(['send_lo', 'send_hi']);
const DRAGINO_TIME_SYNC_INTERVAL_KEYS = new Set(['time_sync_interval', 'synctdc']);
const DRAGINO_METADATA_KEYS = new Set(['frequency', 'subBand', 'last_poll']);
const MILESIGHT_TS301_COMMAND_KEYS = new Set([
  'report_interval',
  'button_lock',
  'data_storage_enabled',
  'data_retransmission_enabled',
  'data_retransmission_interval_seconds',
  'screen_enabled',
  'temperature_unit',
  'magnet_alarm_delay_seconds',
  'offset_1',
  'offset_2',
]);

const ALERT_KEY_PATTERN = /^alert_(hi|lo)_\d+$/;
const SERIAL_KEY_PATTERN = /^serial_\d+$/;
const DRAGINO_OFFSET_KEY_PATTERN = /^offset_(?:\d+|RSSI)(?:_[abc])?$/;

export type LoRaWANConfigStatus = 'automated' | 'metadata' | 'unsupported';

export interface LoRaWANConfigSupport {
  status: LoRaWANConfigStatus;
  reason?: string;
}

const LORAWAN_OTA_PROFILES: Record<string, LoRaWANOtaProfile> = {
  dragino_lht65: {
    supportsAutomatedCommands: true,
    estimateCommandCount: (configs) => estimateDraginoCommandCount(configs, 'dragino_lht65'),
  },
  dragino_ltc2: {
    supportsAutomatedCommands: true,
    estimateCommandCount: (configs) => estimateDraginoCommandCount(configs, 'dragino_ltc2'),
  },
  atek_e5: {
    supportsAutomatedCommands: true,
  },
  milesight_ts301: {
    supportsAutomatedCommands: true,
    estimateCommandCount: estimateMilesightTs301CommandCount,
  },
  milesight_em300di: {
    supportsAutomatedCommands: false,
  },
};

function getChangedConfigs(configs: TransmitterConfig[]): TransmitterConfig[] {
  return configs.filter((config) => config.s !== undefined && config.s !== config.v);
}

function summarizeConfigSupport(
  configs: TransmitterConfig[],
  deviceType?: string,
) {
  const changed = getChangedConfigs(configs);
  let automated = 0;
  let metadata = 0;
  let unsupported = 0;

  for (const config of changed) {
    const support = getLoRaWANConfigSupport(config, deviceType);
    if (support.status === 'automated') automated += 1;
    else if (support.status === 'metadata') metadata += 1;
    else unsupported += 1;
  }

  return { changed, automated, metadata, unsupported };
}

function estimateDraginoCommandCount(
  configs: TransmitterConfig[],
  deviceType: 'dragino_lht65' | 'dragino_ltc2',
): number {
  const changed = getChangedConfigs(configs);
  let count = 0;

  if (changed.some((config) => DRAGINO_INTERVAL_KEYS.has(config.k))) {
    count++;
  }

  if (changed.some((config) => DRAGINO_TIME_SYNC_INTERVAL_KEYS.has(config.k))) {
    count++;
  }

  for (const config of changed) {
    if (DRAGINO_COMMON_COMMAND_KEYS.has(config.k)) count++;
    if (deviceType === 'dragino_lht65' && DRAGINO_LHT65_COMMAND_KEYS.has(config.k)) {
      count++;
    }
  }

  const alertChannels = new Set<string>();
  for (const config of changed) {
    const match = config.k.match(ALERT_KEY_PATTERN);
    if (match) {
      alertChannels.add(config.k.slice(config.k.lastIndexOf('_') + 1));
    }
  }
  count += alertChannels.size;

  return count;
}

function estimateMilesightTs301CommandCount(configs: TransmitterConfig[]): number {
  const changed = getChangedConfigs(configs);
  let count = 0;

  for (const config of changed) {
    if (MILESIGHT_TS301_COMMAND_KEYS.has(config.k)) count++;
  }

  return count;
}

function formatIntervalLabel(seconds: number): string {
  const minutes = seconds / 60;
  if (minutes < 60) {
    return `${Math.max(1, Math.round(minutes))} min`;
  }

  const hours = Math.round((minutes / 60) * 10) / 10;
  return `${hours} hr`;
}

export function getLoRaWANConfigSupport(
  configOrKey: TransmitterConfig | string,
  deviceType?: string,
): LoRaWANConfigSupport {
  const key = typeof configOrKey === 'string' ? configOrKey : configOrKey.k;
  if (!key.trim()) return { status: 'automated' };

  if (deviceType === 'dragino_lht65' || deviceType === 'dragino_ltc2') {
    if (
      DRAGINO_INTERVAL_KEYS.has(key)
      || DRAGINO_COMMON_COMMAND_KEYS.has(key)
      || DRAGINO_TIME_SYNC_INTERVAL_KEYS.has(key)
      || (deviceType === 'dragino_lht65' && DRAGINO_LHT65_COMMAND_KEYS.has(key))
      || ALERT_KEY_PATTERN.test(key)
    ) {
      return { status: 'automated' };
    }

    if (
      SERIAL_KEY_PATTERN.test(key)
      || DRAGINO_OFFSET_KEY_PATTERN.test(key)
      || DRAGINO_METADATA_KEYS.has(key)
    ) {
      return {
        status: 'metadata',
        reason: 'Device-reported metadata. Not sent as a LoRaWAN OTA downlink.',
      };
    }

    return {
      status: 'unsupported',
      reason: 'Not part of the current Dragino LoRaWAN OTA command set.',
    };
  }

  if (deviceType === 'milesight_ts301') {
    if (MILESIGHT_TS301_COMMAND_KEYS.has(key)) {
      return { status: 'automated' };
    }

    if (SERIAL_KEY_PATTERN.test(key)) {
      return {
        status: 'metadata',
        reason: 'Device-reported Probe ID metadata. Not sent as a LoRaWAN OTA downlink.',
      };
    }

    return {
      status: 'unsupported',
      reason: 'Not part of the current Milesight TS301 LoRaWAN OTA command set.',
    };
  }

  return { status: 'automated' };
}

export function estimateLoRaWANCommandCount(configs: TransmitterConfig[], deviceType?: string): number {
  if (!deviceType) return 0;

  const profile = LORAWAN_OTA_PROFILES[deviceType];
  if (!profile?.estimateCommandCount) return 0;

  return profile.estimateCommandCount(configs);
}

export function estimateOtaDuration(
  commandCount: number,
  transmissionIntervalSeconds?: number,
): string | null {
  if (!transmissionIntervalSeconds || transmissionIntervalSeconds <= 0 || commandCount <= 0) {
    return null;
  }

  const totalMinutes = Math.round((commandCount * transmissionIntervalSeconds) / 60);
  if (totalMinutes < 60) return `~${Math.max(1, totalMinutes)} min`;

  const hours = Math.round((totalMinutes / 60) * 10) / 10;
  return `~${hours} hr`;
}

function buildPeriodicButtonTitle(transmissionIntervalSeconds?: number): string {
  if (!transmissionIntervalSeconds || transmissionIntervalSeconds <= 0) {
    return 'LoRaWAN devices receive one command per uplink cycle. Multiple config changes are queued by priority and applied over future uplink cycles; exact timing depends on the device check-in interval.';
  }

  return `LoRaWAN devices receive one command per uplink cycle (about ${formatIntervalLabel(transmissionIntervalSeconds)} between uplinks). Multiple config changes are queued by priority and applied over several cycles.`;
}

export function getLoRaWANOtaNotice(
  configs: TransmitterConfig[],
  options: LoRaWANOtaNoticeOptions = {},
): LoRaWANOtaNotice {
  const { deviceType, transmissionIntervalSeconds } = options;
  const { changed, metadata, unsupported } = summarizeConfigSupport(configs, deviceType);

  if (!deviceType) {
    return {
      buttonTitle:
        'LoRaWAN config updates are delivered over future uplink cycles. Exact timing depends on the device model and uplink cadence.',
      pendingText:
        'Config update queued - delivery timing depends on the device model and uplink cadence.',
    };
  }

  const profile = LORAWAN_OTA_PROFILES[deviceType];
  if (!profile) {
    return {
      buttonTitle:
        'LoRaWAN config updates are delivered over future uplink cycles. Exact timing depends on the device model and uplink cadence.',
      pendingText:
        'Config update queued - delivery timing depends on the device model and uplink cadence.',
    };
  }

  if (deviceType === 'atek_e5') {
    const timingDetail =
      transmissionIntervalSeconds && transmissionIntervalSeconds > 0
        ? `timing follows the device check-in interval (about ${formatIntervalLabel(transmissionIntervalSeconds)} between uplinks)`
        : 'timing follows the device check-in interval';

    return {
      buttonTitle:
        `ATEK E5 applies chunked CBOR config over future uplink cycles. Exact command count depends on payload size, and ${timingDetail}.`,
      pendingText:
        changed.length > 0
          ? 'Config update queued — chunked ATEK config will be applied over future uplink cycles (exact command count depends on payload size).'
          : 'Config update queued — transmitter will apply on next check-in.',
    };
  }

  if (!profile.supportsAutomatedCommands) {
    return {
      buttonTitle:
        'This LoRaWAN model is event-driven and does not have automated OTA config commands in the current flow.',
      pendingText:
        'Config update queued — this model has no automated LoRaWAN config commands in the current flow.',
    };
  }

  const commandCount = estimateLoRaWANCommandCount(configs, deviceType);
  const buttonTitle = buildPeriodicButtonTitle(transmissionIntervalSeconds);

  if (commandCount <= 0) {
    if (metadata > 0 && unsupported === 0) {
      return {
        buttonTitle:
          'Some displayed fields are device-reported metadata and are not sent as LoRaWAN OTA commands.',
        pendingText:
          'Changed keys are metadata-only and will not be sent over LoRaWAN.',
      };
    }

    if (unsupported > 0) {
      return {
        buttonTitle:
          'Some changed keys are not part of the current LoRaWAN OTA command set for this model.',
        pendingText:
          'Changed keys are not part of the current LoRaWAN OTA flow and will not be sent over LoRaWAN.',
      };
    }

    return {
      buttonTitle,
      pendingText: 'Config update queued — transmitter will apply on next check-in.',
    };
  }

  const duration = estimateOtaDuration(commandCount, transmissionIntervalSeconds);
  const commandLabel = `command${commandCount === 1 ? '' : 's'}`;

  if (duration && transmissionIntervalSeconds && transmissionIntervalSeconds > 0) {
    return {
      buttonTitle,
      pendingText:
        `Config update queued — ${commandCount} ${commandLabel} will be applied over ${duration} (about ${formatIntervalLabel(transmissionIntervalSeconds)} per uplink cycle).`,
    };
  }

  return {
    buttonTitle,
    pendingText:
      `Config update queued — ${commandCount} ${commandLabel} will be applied over future uplink cycles (device interval varies).`,
  };
}
